/**
 * Shows activity tracking dropdown and manages activities.
 * @constructor
 * @param {array} activities - List of activities
 * @param {string} activityCountId - Id to show total Activities count
 * @param {string} activityDropDownId - Id to bind hide and show activity dropdown
 */

/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/activity-tracking/activity-tracking.css" />
 */

import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import templates from "./template.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { operationStatus } from "/WebInterface/new-ui/assets/js/constants.js";

export default class ActivityTracking {
  constructor({ activityCountId, activityDropDownId }) {
    this.activities = [];
    this.activityCountId = activityCountId;
    this.activityDropDownId = activityDropDownId;
    this.activityElements = new Map();
    this.init();
  }

  init = () => {
    this.createActivityModal();
    this.createActivity();
  };

  getStatusById = (id) => {
    const activity = this.activities.find((item) => item.id === id);
    return activity ? activity.status : null;
  };

  createOrUpdate = (activity = {}) => {
    if (activity.id) {
      const curActivity = this.activities.find(
        (item) => item.id === activity.id
      );
      if (activity.show) this.show();
      if (curActivity) {
        this.update(activity);
        return curActivity.id;
      } else {
        return this.add(activity);
      }
    } else {
      return this.add(activity);
    }
  };

  add = (activity = {}) => {
    const id = activity.id || utils.getUId();
    const newActivity = {
      hasProgressBar: true,
      cancelable: true,
      ...activity,
      id,
    };
    this.activities.push(newActivity);
    this.renderActivityItem(newActivity);
    this.updateActivityCount();
    return id;
  };

  update = (updatedActivity = {}) => {
    const indexOfActivityToBeUpdated = this.activities.findIndex(
      (item) => item.id === updatedActivity.id
    );

    if (indexOfActivityToBeUpdated !== -1) {
      this.activities[indexOfActivityToBeUpdated] = {
        ...this.activities[indexOfActivityToBeUpdated],
        ...updatedActivity,
      };
      this.renderActivityItem(this.activities[indexOfActivityToBeUpdated]);
    }

    this.updateActivityCount();
  };

  remove = (id) => {
    let hasErrorActivities = false;

    this.activities = this.activities.filter((item) => {
      if (item.status === operationStatus.FAILED && item.id !== id) {
        hasErrorActivities = true;
      }
      return item.id !== id;
    });

    const existingEl = this.activityElements.get(id);
    if (existingEl) {
      existingEl.remove();
      this.activityElements.delete(id);
    }

    const activityCountEl = _$(`#${this.activityCountId}`);
    if (hasErrorActivities) {
      activityCountEl.addClass("error");
    } else {
      activityCountEl.removeClass("error");
    }

    if (this.activities.length === 0) {
      this.createActivity();
    } else {
      this.updateActivityCount();
    }
  };

  createActivityModal = () => {
    this.activityModal = utils.createElement("div", ["activity-section"]);
    document.body.append(this.activityModal);
    const activityList = this.createActivityList();
    this.activityModal.append(activityList);
    return this.activityModal;
  };

  createActivityList = () => {
    this.activityList = utils.createElement("div", ["activity-list"]);
    return this.activityList;
  };

  buildActivityElement = (element) => {
    const {
      type,
      message,
      cancelable,
      hasProgressBar,
      currentProgress,
      onCancel,
      onClick,
      maxProgress = 100,
      hasInfiniteLoader = true,
      status,
    } = element;

    let manipulatedCurrentProgress = currentProgress;
    let manipulatedCurrentProgressPercentage = parseInt(
      (currentProgress / maxProgress) * 100
    );

    if (currentProgress > maxProgress) {
      manipulatedCurrentProgress = maxProgress;
    }

    const { COMPLETED, FAILED, CANCELED } = operationStatus;
    const noProgressStatuses = [COMPLETED, FAILED, CANCELED];

    const completed = noProgressStatuses.find(
      (noProgressStatus) => status === noProgressStatus
    );

    let activity = utils.createElement(
      "div",
      ["activity", status || null],
      [],
      templates.activityTemplate({
        type,
        maxProgress,
        currentProgress: manipulatedCurrentProgress,
        currentProgressPercentage: manipulatedCurrentProgress
          ? manipulatedCurrentProgressPercentage + "%"
          : "",
      })
    );

    activity.setAttribute("data-activity-id", element.id);

    const messageEl = activity.querySelector(".subtitle");
    if (messageEl) {
      messageEl.textContent = message || "";
    }

    if (status === FAILED) {
      const activityCountEl = _$(`#${this.activityCountId}`);
      activityCountEl.addClass("error");
    }

    if (typeof onClick === "function") {
      activity.addEventListener("click", (e) => {
        e.stopPropagation();
        onClick(element);
      });
    }

    if (cancelable) {
      const cancelButton = activity.querySelector(".cancel-button");
      if (cancelButton) {
        cancelButton.classList.remove("is-hidden");
        cancelButton.addEventListener("click", (e) => {
          e.stopPropagation();
          onCancel(element);
        });
      }
    }

    if (hasProgressBar && !completed) {
      const progressBar = activity.querySelector(".progress-bar");
      progressBar.classList.remove("is-hidden");
    }

    if (hasInfiniteLoader) {
      const progressBar = activity.querySelector(".progress");
      const completedPercentage = activity.querySelector(
        ".completed-percentage"
      );
      completedPercentage.classList.add("is-hidden");
      progressBar.removeAttribute("value");
    }

    return activity;
  };

  renderActivityItem = (element) => {
    if (!this.activityList) {
      return;
    }

    const emptyState = this.activityList.querySelector("[data-i18n-key]");
    if (emptyState) {
      this.activityList.innerHTML = "";
      this.activityElements.clear();
    }

    const existingEl = this.activityElements.get(element.id);
    const activity = this.buildActivityElement(element);

    if (existingEl && existingEl.parentNode === this.activityList) {
      this.activityList.replaceChild(activity, existingEl);
    } else {
      this.activityList.append(activity);
    }

    this.activityElements.set(element.id, activity);
  };

  updateActivityCount = () => {
    if (!(this.activityCountId && _$(`#${this.activityCountId}`))) {
      return;
    }

    const activityCountEl = _$(`#${this.activityCountId}`).el();
    if (this.activities.length > 0) {
      activityCountEl.classList.remove("is-hidden");
      activityCountEl.innerText = this.activities.length;
    } else {
      activityCountEl.classList.add("is-hidden");
    }
  };

  createActivity = () => {
    if (this.activities.length > 0) {
      const scrollTopBeforeRendering = this.activityList.scrollTop;
      this.activityList.innerHTML = "";
      this.activityElements.clear();

      this.activities.forEach((element) => {
        const activity = this.buildActivityElement(element);
        this.activityList.append(activity);
        this.activityElements.set(element.id, activity);
      });

      this.activityList.scrollTop = scrollTopBeforeRendering;
      this.updateActivityCount();
    } else {
      this.activityElements.clear();
      if (this.activityCountId && _$(`#${this.activityCountId}`)) {
        const activityCountEl = _$(`#${this.activityCountId}`).el();
        activityCountEl.classList.add("is-hidden");

        this.activityList.innerHTML = templates.emptyTemplate({
          i18nKey: "nothingToDisplay",
          nothingToDisplayText: t("nothingToDisplay"),
        });
      }
    }
  };

  trigger = (type, id) => {
    const activity = this.activities.find((item) => item.id === id);

    if (activity) {
      if (typeof activity.onClick === "function" && type === "click") {
        activity.onClick(activity);
      }
      if (typeof activity.onCancel === "function" && type === "cancel") {
        activity.onCancel(activity);
      }
    }
  };

  show = () => {
    if (
      this.activityDropDownId &&
      !_$(`#${this.activityDropDownId}`).hasClass("is-active")
    ) {
      _$(`#${this.activityDropDownId}`).find(`.dropdown-trigger`).el().click();
    }
  };

  hide = () => {
    if (
      this.activityDropDownId &&
      _$(`#${this.activityDropDownId}`).hasClass("is-active")
    ) {
      _$(`#${this.activityDropDownId}`).find(`.dropdown-trigger`).el().click();
    }
  };
}
